'use client';
import { useEffect } from 'react';
import Header from '@/components/common/Header';
import Footer from '@/components/common/Footer';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import Link from 'next/link';

export default function BlogPage() {
  const featuredPost = {
    id: 'featured_wood_pellets',
    title: "The Future of Sustainable Wood Pellets in 2026",
    excerpt: "Eco-friendly sourcing is changing the biomass energy industry. Learn what it means for businesses investing in renewable heating solutions. We cover the latest trends, certifications, and best practices.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_14c4dd2c1-1768255287690.png",
    alt: "Sustainable wood pellets production facility with eco-friendly practices",
    category: "Sustainability",
    date: "February 10, 2026",
    readTime: "8 min read",
    href: "/blog#sustainable-wood-pellets"
  };

  const posts = [
  {
    id: 'post_cooking_oils',
    title: "Top 5 Cooking Oils in High Demand for 2026",
    excerpt: "Which cooking oils are food manufacturers buying right now? We break down the top 5 and explain why they're essential for your business.",
    image: "https://images.unsplash.com/photo-1679604129971-0611abd53ff5",
    alt: "Various cooking oil bottles showing market trends",
    category: "Market Trends",
    date: "February 8, 2026",
    readTime: "5 min read",
    href: "/blog#cooking-oils-2026"
  },
  {
    id: 'post_chicken_feed',
    title: "How to Choose Quality Chicken Feed for Your Farm",
    excerpt: "Not all chicken feed is created equal. This guide helps you select the right formulation for layers, broilers, and organic operations.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a79c2185-1770939795268.png",
    alt: "Chicken feed selection guide showing different feed types",
    category: "Agriculture",
    date: "February 5, 2026",
    readTime: "6 min read",
    href: "/blog#chicken-feed-guide"
  },
  {
    id: 'post_npk',
    title: "NPK Fertilizers: Understanding the Numbers",
    excerpt: "What do those numbers on fertilizer bags mean? We explain nitrogen, phosphorus, and potassium ratios and how to choose the right formula.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_17bc3ff46-1765051026926.png",
    alt: "NPK fertilizer granules showing balanced crop nutrition",
    category: "Education",
    date: "February 1, 2026",
    readTime: "7 min read",
    href: "/blog#npk-fertilizers"
  },
  {
    id: 'post_iron_recycling',
    title: "Iron Scrap Recycling: Environmental Impact",
    excerpt: "How recycling iron scrap benefits the planet and your bottom line. Learn about the economics of scrap metal recycling.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1149fd5e5-1770939796960.png",
    alt: "Iron scrap recycling showing environmental benefits",
    category: "Sustainability",
    date: "January 28, 2026",
    readTime: "5 min read",
    href: "/blog#iron-recycling"
  },
  {
    id: 'post_supply_chain',
    title: "Global Supply Chain Trends in 2026",
    excerpt: "Key trends shaping supply and distribution this year. From logistics innovations to geopolitical factors affecting trade.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_14abedf49-1767851496109.png",
    alt: "Global supply chain operations showing modern logistics",
    category: "Industry News",
    date: "January 25, 2026",
    readTime: "6 min read",
    href: "/blog#supply-chain-trends"
  },
  {
    id: 'post_bulk_ordering',
    title: "Bulk Ordering Tips for Businesses",
    excerpt: "Maximize savings and efficiency with smart ordering strategies. Learn about volume discounts and inventory management.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a0366a63-1770939795061.png",
    alt: "Bulk ordering warehouse showing efficient inventory management",
    category: "Business Tips",
    date: "January 20, 2026",
    readTime: "5 min read",
    href: "/blog#bulk-ordering"
  },
  {
    id: 'post_certifications',
    title: "Quality Certifications: What They Mean for You",
    excerpt: "Understanding ISO and other certifications in the supply industry. Why they matter and how they protect your business.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_15949e003-1764671142241.png",
    alt: "Quality certification documents showing industry standards",
    category: "Quality Standards",
    date: "January 15, 2026",
    readTime: "6 min read",
    href: "/blog#quality-certifications"
  },
  {
    id: 'post_wood_storage',
    title: "Wood Pellet Storage Best Practices",
    excerpt: "Keep your wood pellets dry and efficient with proper storage. Prevent moisture damage and maintain quality year-round.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b4244ce5-1770939798074.png",
    alt: "Wood pellet storage facility showing best practices",
    category: "How-To",
    date: "January 10, 2026",
    readTime: "4 min read",
    href: "/blog#wood-storage"
  },
  {
    id: 'post_success_stories',
    title: "Meet Our Partners: Success Stories",
    excerpt: "How our partners are thriving with SDSA. Real case studies from businesses in Brazil, Morocco, Netherlands, and beyond.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1c48cf213-1766492155535.png",
    alt: "Business partners celebrating successful partnership",
    category: "Case Studies",
    date: "January 5, 2026",
    readTime: "8 min read",
    href: "/blog#success-stories"
  }];


  const categories = [
  'All Posts',
  'Market Trends',
  'Agriculture',
  'Sustainability',
  'Industry News',
  'Business Tips',
  'How-To',
  'Case Studies',
  'Quality Standards',
  'Education'];


  useEffect(() => {
    const reveals = document.querySelectorAll('.reveal');
    if (!reveals?.length) return;

    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add('active')),
      { threshold: 0.1 }
    );
    reveals?.forEach((el) => observer?.observe(el));

    return () => observer?.disconnect();
  }, []);

  return (
    <div className="min-h-screen">
      <Header />
      <main className="py-24 bg-background">
        <div className="max-w-container mx-auto px-6 md:px-12">
          {/* Page Header */}
          <div className="text-center mb-16 reveal">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-foreground mb-6">
              Industry Insights & News
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Stay updated with the latest from the supply and distribution world. Expert insights, market trends, and practical guides.
            </p>
          </div>

          {/* Featured Post */}
          <div className="mb-16 reveal">
            <Link href={featuredPost?.href} className="group block">
              <article className="relative h-[500px] rounded-organic-xl overflow-hidden shadow-xl hover-lift">
                <AppImage
                  src={featuredPost?.image}
                  alt={featuredPost?.alt}
                  className="w-full h-full object-cover image-scale" />
                
                
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />

                {/* Content */}
                <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12 text-white">
                  <span className="inline-block px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider bg-primary text-primary-foreground mb-4">
                    {featuredPost?.category}
                  </span>
                  
                  <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold mb-4 leading-tight group-hover:text-primary-foreground transition-colors">
                    {featuredPost?.title}
                  </h2>
                  
                  <p className="text-lg text-gray-300 mb-6 max-w-3xl leading-relaxed">
                    {featuredPost?.excerpt}
                  </p>

                  <div className="flex items-center gap-6 text-sm">
                    <div className="flex items-center gap-2">
                      <Icon name="CalendarIcon" size={16} />
                      <span>{featuredPost?.date}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Icon name="ClockIcon" size={16} />
                      <span>{featuredPost?.readTime}</span>
                    </div>
                    <div className="inline-flex items-center gap-2 font-semibold">
                      Read Full Article
                      <Icon name="ArrowRightIcon" size={16} className="arrow-shift" />
                    </div>
                  </div>
                </div>
              </article>
            </Link>
          </div>

          {/* Recent Posts Grid */}
          <div className="mb-16">
            <h2 className="text-3xl font-serif font-bold text-foreground mb-8 reveal">
              Recent Articles
            </h2>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts?.map((post, index) =>
              <Link
                key={post?.id}
                href={post?.href}
                className="group reveal"
                style={{ transitionDelay: `${index * 50}ms` }}>
                
                  <article className="bg-card rounded-organic overflow-hidden shadow-sm hover-lift h-full">
                    {/* Image with Date Badge */}
                    <div className="relative h-56 overflow-hidden">
                      <AppImage
                      src={post?.image}
                      alt={post?.alt}
                      className="w-full h-full object-cover image-scale" />
                    
                      
                      {/* Date Badge */}
                      <div className="absolute top-4 left-4 bg-white/95 backdrop-blur-sm rounded-lg px-3 py-2 text-center shadow-md">
                        <div className="text-lg font-bold text-foreground leading-none">
                          {post?.date?.split(' ')?.[1]?.replace(',', '')}
                        </div>
                        <div className="text-xs uppercase font-semibold text-muted-foreground">
                          {post?.date?.split(' ')?.[0]?.slice(0, 3)}
                        </div>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-6">
                      {/* Category Pill */}
                      <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider bg-primary/10 text-primary mb-3">
                        {post?.category}
                      </span>

                      <h3 className="text-xl font-bold font-serif text-foreground mb-3 leading-tight group-hover:text-primary transition-colors line-clamp-2">
                        {post?.title}
                      </h3>

                      <p className="text-sm text-muted-foreground mb-4 leading-relaxed line-clamp-3">
                        {post?.excerpt}
                      </p>

                      {/* Metadata */}
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Icon name="ClockIcon" size={14} />
                          <span>{post?.readTime}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Icon name="CalendarIcon" size={14} />
                          <span>{post?.date}</span>
                        </div>
                      </div>

                      {/* Read More Link */}
                      <div className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-primary">
                        Read More
                        <Icon name="ArrowRightIcon" size={14} className="arrow-shift" />
                      </div>
                    </div>
                  </article>
                </Link>
              )}
            </div>
          </div>

          {/* Newsletter Signup */}
          <section className="bg-gray-900 rounded-organic-xl p-8 md:p-12 text-center text-white reveal">
            <Icon name="EnvelopeIcon" size={48} className="mx-auto mb-4 opacity-80" />
            <h3 className="text-3xl md:text-4xl font-serif font-bold mb-4">
              Stay Updated
            </h3>
            <p className="text-lg opacity-90 mb-8 max-w-2xl mx-auto">
              Get industry insights and market updates delivered to your inbox. Subscribe to our newsletter.
            </p>
            
            <form className="max-w-md mx-auto flex gap-3">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-6 py-3 rounded-full bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-primary" />
              
              <button
                type="submit"
                className="px-8 py-3 bg-primary text-primary-foreground rounded-full font-semibold hover:bg-primary/90 transition-colors btn-scale">
                
                Subscribe
              </button>
            </form>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );

}